import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { map } from "rxjs/operators";
import { IDte } from "src/app/models/Dte/IDte";
import { DteEmitido } from "src/app/models/Dte/DteEmitido";
import { InvoiceType } from "src/app/models/Dte/invoiceType.enum";
import { NotaCredito } from "src/app/models/NotaCredito";
import { NotaDebito } from "src/app/models/NotaDebito";
import { FacturacionIndividualService } from "src/app/services/facturacion-individual.service";
import { prepararPayloadExportacion, prepararPayloadLiquidacion } from "src/app/components/facturacion/facturador-individual/dte-payload/dte-payload.factory";

export interface DteEmissionRequest {
	invoiceType: InvoiceType;
	usuarioId: string;
	sucursalId: string;
	dte: IDte;
	notaCredito?: NotaCredito;
	notaDebito?: NotaDebito;
}

export interface DteEmissionResult {
	dteId: string;
	titulo: string;
	mensaje: string;
	reiniciarFormulario: boolean;
}

@Injectable({ providedIn: "root" })
export class DteEmissionService {
	constructor(private facturadorApi: FacturacionIndividualService) {}

	emitir(request: DteEmissionRequest): Observable<DteEmissionResult> {
		const contexto = {
			usuarioId: request.usuarioId,
			sucursalId: request.sucursalId,
		};

		switch (request.invoiceType) {
			case InvoiceType.Factura:
				return this.facturadorApi.sendDte(contexto.usuarioId, contexto.sucursalId, request.dte).pipe(map((response) => this.resultado(response.data, "Documento Emitido", "El documento se ha emitido correctamente", true)));
			case InvoiceType.GuiaDespacho:
				return this.facturadorApi.sendDteGuide(contexto.usuarioId, contexto.sucursalId, request.dte).pipe(map((response) => this.resultado(response.data, "Documento Emitido", "El documento se ha emitido correctamente", true)));
			case InvoiceType.LiquidacionFactura:
				return this.facturadorApi.sendDteSettlement(contexto.usuarioId, contexto.sucursalId, prepararPayloadLiquidacion(request.dte)).pipe(map((response) => this.resultado(response.data, "Documento Emitido", "El documento se ha emitido correctamente", true)));
			case InvoiceType.Boleta:
				request.dte.sucursalId = contexto.sucursalId;
				return this.facturadorApi.sendDteBoleta(contexto.usuarioId, contexto.sucursalId, request.dte).pipe(map((response) => this.resultado(response.data, "Boleta Emitida", "La boleta se ha emitido correctamente", false)));
			case InvoiceType.FacturaCompra:
				return this.facturadorApi.sendFacturaCompraRequest(contexto.usuarioId, contexto.sucursalId, request.dte).pipe(map((response) => this.resultado(response.data, "Factura de Compra Emitida", "La factura de compra se ha emitido correctamente", false)));
			case InvoiceType.FacturaExportacion:
				return this.facturadorApi.sendFacturaExportacion(contexto.usuarioId, contexto.sucursalId, prepararPayloadExportacion(request.dte)).pipe(map((response) => this.resultado(response.data, "Factura de Exportación Emitida", "La factura de exportación se ha emitido correctamente", false)));
			case InvoiceType.NotaDebito:
				if (!request.notaDebito) return throwError(new Error("Faltan los datos de la nota de débito"));
				return this.facturadorApi.sendNotaDebito(contexto.usuarioId, contexto.sucursalId, request.notaDebito, request.dte as DteEmitido).pipe(map((response) => this.resultado(response.data.dteId, "Nota de Débito Emitida", "La nota de débito se ha emitido correctamente.", false)));
			case InvoiceType.NotaCredito:
				if (!request.notaCredito) return throwError(new Error("Faltan los datos de la nota de crédito"));
				return this.facturadorApi.sendNotaCredito(contexto.usuarioId, contexto.sucursalId, request.notaCredito, request.dte as DteEmitido).pipe(map((response) => this.resultado(response.data.dteId, "Nota de Crédito Emitida", "La nota de crédito se ha emitido correctamente.", false)));
			default:
				return throwError(new Error("Tipo de documento no soportado"));
		}
	}

	private resultado(dteId: string, titulo: string, mensaje: string, reiniciarFormulario: boolean): DteEmissionResult {
		return { dteId, titulo, mensaje, reiniciarFormulario };
	}
}
